/* =========================================================================
   FRIENDLY PLAY — the education-only study mode (PLAN-gameplay-round2.md
   Part 2). Trivia "without the score, points and $$": pick a study area and a
   level, answer at your own pace, read the citation, keep going or stop.

   SHAPE. A small sibling controller in the arcade.ts mould — it borrows the
   shared pieces (RoundEngine for serve/shuffle/correctness, the play screen's
   card classes for the look) and sandboxes the rest. It deliberately does NOT
   thread a "practice" flag through gameController.ts, which welds the question
   card to the drain meter, the timeout timer, break dispatch, DYK interludes,
   the resume snapshot and the results screen — the plan calls that out by name
   as too invasive for this, and the scored game is the thing that must not
   break.

   THE HARD NO-$ RULE (plan §2a). Nothing in this flow may call fmtCents /
   fmtDelta (engine.ts), ctx.updateBank, ctx.floatDelta or awardBonus, and
   #bankbar stays hidden throughout (the same mechanism the Arcade uses for
   guests). The engine's own bankCents still ticks internally on submitAnswer —
   it is never read, never shown, never posted. This file's own engine instance
   is local: ctx.engine (the scored run, possibly mid-resume) is never touched.

   NOT INCLUDED, on purpose:
     • no timer, no drain, no timeout, no penalty — untimed is the whole point;
     • no breaks / mini-games (all four are $-paying; the Arcade has them);
     • no streak, no wallet, no session post/GameSessions row — a study set
       still changes nothing about the SCORED game's state;
     • no servedLog recordServed() — /api/log/served feeds content-calibration
       counts for REAL play, and study traffic would silently skew them (same
       reason the telemetry beats below carry mode:'friendly').

   XP (2026-08-03, Eric: "they can build XPs but not money in this mode"):
   showEndCard() posts correct/answered to POST /game/friendly-xp, which credits
   UserStats.Xp ONLY (usp_Stats_AwardXp, db/038) — no GameSessions row, no
   streak, no ledger. Guests never call it (no account to credit).

   PERFECT-SET CELEBRATION (2026-08-04, Eric: "play the Brother Theo Confetti
   / celebration animation when someone gets all questions in a section
   right"): a set with every answer correct gets a confetti burst (the .rc /
   rcFall pattern feast.ts's celebrate() and gameController.ts's
   roundIntroConfetti() already use — reused here, not re-invented) plus
   Theo's corner pop with a 'correct' line. No new account state — this is
   purely a moment, same as everywhere else Theo pops in.

   GUESTS: fully allowed. No isAuthenticated() gate anywhere — the bundled
   packs already make the app guest-playable, and there is no economy here.
   ========================================================================= */
import { RoundEngine, esc, type ServedQuestion } from './engine';
import { icon } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { stableCodeFor } from './data/questionCodes';
import { isFriendlyPlayEnabled } from './featureFlags';
import { maybeShowTutorial } from './tutorialOverlay';
import { awardFriendlyXp, isAuthenticated } from './api';
import { triggerCharPop } from './characters/ui';
import { getLine } from './characters/registry';
import './characters/theo'; // side-effect: registers Brother Theo before any getLine()
import {
  decideTheoPop,
  freshTheoState,
  armPonderTimer,
  clearPonderTimer,
  type TheoState,
  type TheoEvent,
} from './friendlyTheo';
import {
  FRIENDLY_SET_SIZE,
  FRIENDLY_XP_PER_CORRECT,
  LEVEL_CHOICES,
  bundledStudyAreas,
  bundledStudyItems,
  countFor,
  encouragement,
  isFullyServed,
  levelLabel,
  nextStudyArea,
  poolFor,
  scoreLine,
  sessionLabel,
  type LevelChoice,
  type StudyArea,
  type StudyItem,
} from './friendlyCatalog';

/* ------------------------------ session state ------------------------------ */

interface Session {
  area: string;
  choice: LevelChoice;
  engine: RoundEngine;
  correct: number;
  answered: number;
}

let session: Session | null = null;
let awaitingAnswer = false;
let theoState: TheoState = freshTheoState(); // per-session pop-in budget (friendlyTheo.ts)

/** Run an event through the pop scheduler and show the line if it earns one. */
function maybeTheoPop(ev: TheoEvent): void {
  const kind = decideTheoPop(ev, theoState, session?.answered ?? 0);
  if (kind) triggerCharPop(getLine('theo', kind));
}

/* --------------------------- "fully served" tracking ------------------------ */

/** Every bundled question id ever SERVED (shown) in Friendly Play, across every
    area/level/session — a plain localStorage set, same family as
    catholics.tutorialSeen.<gameKey>. A chip's ✓ (levelChipHTML) means every
    question in that area × level pool has appeared here at least once, which
    can take more than one set — a partial set does NOT earn the checkmark
    (Eric, 2026-08-02: replaces the old "attempted once" semantics). */
const SEEN_KEY = 'catholics.friendlySeenQuestions';

function readSeen(): Set<number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
  } catch {
    return new Set();
  }
}

function markSeen(questionId: number): void {
  try {
    const s = readSeen();
    s.add(questionId);
    localStorage.setItem(SEEN_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode / eviction — the checkmark just won't persist */
  }
}

/** Perfect-set confetti burst (reuses the .rc / rcFall pattern feast.ts's
    celebrate() and gameController.ts's roundIntroConfetti() already use). */
function friendlyConfetti(wrap: HTMLElement): void {
  wrap.innerHTML = '';
  const marks = ['📖', '✨', '⭐', '📖'];
  for (let i = 0; i < 14; i++) {
    const s = document.createElement('div');
    s.className = 'rc';
    s.textContent = marks[i % marks.length];
    s.style.left = (5 + Math.random() * 90).toFixed(1) + '%';
    s.style.fontSize = (15 + Math.random() * 16).toFixed(0) + 'px';
    s.style.animationDelay = (Math.random() * 0.55).toFixed(2) + 's';
    s.style.setProperty('--rcr', (Math.random() * 360 - 180).toFixed(0) + 'deg');
    wrap.appendChild(s);
  }
}

/** The bank bar belongs to scored play; Friendly Play never shows it. */
function hideBank(): void {
  try {
    byId('bankbar').classList.add('hidden');
  } catch {
    /* no shell mounted (tests) */
  }
}

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function setHidden(id: string, hidden: boolean): void {
  el(id)?.classList.toggle('hidden', hidden);
}

/* --------------------------------- picker --------------------------------- */

function levelChipHTML(items: readonly StudyItem[], seen: ReadonlySet<number>, area: StudyArea, choice: LevelChoice): string {
  const n = countFor(area, choice);
  const dis = n === 0 ? ' disabled' : '';
  const key = choice === 'all' ? 'all' : String(choice);
  const done = isFullyServed(items, area.name, choice, seen) ? ' done' : '';
  const check = done ? '<span class="fr-check" aria-label="Every question here has been served">✓</span>' : '';
  return (
    `<button class="fr-level${choice === 'all' ? ' all' : ''}${done}" type="button"` +
    ` data-area="${esc(area.name)}" data-level="${key}"${dis}>` +
    check +
    `<span class="fr-level-n">${levelLabel(choice)}</span>` +
    `<span class="fr-level-c">${n}</span>` +
    `</button>`
  );
}

function rowHTML(items: readonly StudyItem[], seen: ReadonlySet<number>, area: StudyArea): string {
  return (
    `<div class="fr-row">` +
    `<div class="fr-row-head">` +
    `<span class="fr-ic">${esc(area.icon)}</span>` +
    `<span class="fr-name">${esc(area.name)}</span>` +
    `<span class="fr-total">${area.total} question${area.total === 1 ? '' : 's'}</span>` +
    `</div>` +
    `<div class="fr-levels">${LEVEL_CHOICES.map((c) => levelChipHTML(items, seen, area, c)).join('')}</div>` +
    `</div>`
  );
}

/** Paint the study-area list. Counts come straight from the bundled packs
    (friendlyCatalog) — no endpoint, no loading state, works offline. Exported
    (like minigames/wheel.ts's buildWheel) so tests can populate #frList's rows
    without going through the full openFriendlyPlay() show/tutorial flow. */
export function renderPicker(): void {
  const host = el('frList');
  if (!host) return;
  const items = bundledStudyItems();
  const seen = readSeen();
  const areas = bundledStudyAreas();
  host.innerHTML = areas.map((a) => rowHTML(items, seen, a)).join('');
  host.querySelectorAll<HTMLButtonElement>('.fr-level').forEach((b) => {
    b.addEventListener('click', () => {
      const area = b.dataset.area || '';
      const raw = b.dataset.level || 'all';
      const choice: LevelChoice = raw === 'all' ? 'all' : (Number(raw) as 1 | 2 | 3);
      startFriendlySession(area, choice);
    });
  });
}

/* -------------------------------- a session -------------------------------- */

/** The rotating "warm place" hero over the study card (Eric 2026-09-06):
    each session shows the next scene — mockups call for three famous Assisi
    locations (Basilica of St Francis, Basilica of St Clare, a quiet cloister).
    INTERIM ART: crops of the house monastery scene until the real Assisi
    banners can be generated (Higgsfield daily limit); swap the entries below
    when they land — everything else is data-driven. */
export const FRIENDLY_HEROES: readonly { img: string; theo: string; cap: string }[] = [
  { img: '/media/friendly/hero-1.jpg', theo: '/media/characters/theo-welcome.png', cap: 'The bell tower' },
  { img: '/media/friendly/hero-2.jpg', theo: '/media/characters/theo-teach.png', cap: 'The cloister walk' },
  { img: '/media/friendly/hero-3.jpg', theo: '/media/characters/theo-greet.png', cap: 'The monastery garden' },
];
const HERO_IDX_KEY = 'catholics.frHeroIdx';

/** Advance and apply the session hero. Safe with no DOM/storage (tests). */
function applySessionHero(): void {
  let idx = 0;
  try {
    idx = (parseInt(localStorage.getItem(HERO_IDX_KEY) || '0', 10) || 0) % FRIENDLY_HEROES.length;
    localStorage.setItem(HERO_IDX_KEY, String((idx + 1) % FRIENDLY_HEROES.length));
  } catch {
    /* no storage — first scene every time */
  }
  const h = FRIENDLY_HEROES[idx];
  const hero = el('frHero');
  if (hero) hero.style.backgroundImage = `url('${h.img}')`;
  const theo = el('frHeroTheo') as HTMLImageElement | null;
  if (theo) theo.src = h.theo;
  const cap = el('frHeroCap');
  if (cap) cap.textContent = h.cap;
}

/** Begin one study set (~10 questions) for an area × level. Exported for the
    "Keep studying" seam and for any future deep-link. */
export function startFriendlySession(area: string, choice: LevelChoice): void {
  const pool = poolFor(bundledStudyItems(), area, choice);
  if (pool.length === 0) return; // empty tier — the chip is disabled, but be safe
  session = {
    area,
    choice,
    engine: new RoundEngine(pool, FRIENDLY_SET_SIZE, 0),
    correct: 0,
    answered: 0,
  };
  track('FriendlySessionStarted', {
    mode: 'friendly',
    area,
    level: choice,
    count: session.engine.n,
  });
  hideBank();
  setHidden('frEnd', true);
  applySessionHero(); // next warm scene each set
  show('friendlyplay');
  theoState = freshTheoState();
  maybeTheoPop('start');
  serve();
}

function serve(): void {
  const s = session;
  if (!s) return;
  const q = s.engine.serveNext(performance.now()); // untimed: the timestamp is never read back
  awaitingAnswer = true;
  markSeen(q.id); // this question counts toward its tier's ✓ from this moment on
  // "Take your time, friend" — armed per question, cleared on answer/exit.
  armPonderTimer(() => {
    if (awaitingAnswer) maybeTheoPop('idle');
  });

  // Same beat scored play emits (gameController.reveal), plus mode:'friendly'
  // so /sadmin question-stats can exclude study traffic from the %-correct
  // signal used for content calibration.
  track('QuestionServed', {
    mode: 'friendly',
    code: stableCodeFor(q.id),
    category: q.category,
    level: s.choice,
  });

  const area = el('frArea');
  if (area) area.textContent = `${sessionLabel(s.area, s.choice)}`;
  const prog = el('frProg');
  if (prog) prog.textContent = `Question ${s.engine.questionNumber} of ${s.engine.n}`;
  // Segmented progress pills (mockup): answered = filled gold, current = ring.
  const seg = el('frSeg');
  if (seg) {
    const qn = s.engine.questionNumber;
    seg.innerHTML = Array.from({ length: s.engine.n }, (_, i) =>
      `<span class="frh-pill${i < qn - 1 ? ' on' : i === qn - 1 ? ' cur' : ''}"></span>`).join('');
  }
  const body = el('frBody');
  if (body) body.textContent = q.body;
  const reveal = el('frReveal');
  if (reveal) reveal.innerHTML = '';
  setHidden('frNextBtn', true);
  setHidden('frHint', false);
  setHidden('frBody', false);
  setHidden('frOptions', false);

  const grid = el('frOptions');
  if (!grid) return;
  grid.innerHTML = '';
  grid.classList.toggle('long', q.options.some((o) => o.length >= 16));
  q.options.forEach((opt, i) => {
    const b = document.createElement('button');
    b.className = 'opt';
    // Letters, not numbers, in the study room (mockup) — kinder, less scorey.
    b.innerHTML = `<span class="key">${String.fromCharCode(65 + i)}</span><span>${esc(opt)}</span>`;
    b.addEventListener('click', () => onAnswer(i));
    grid.appendChild(b);
  });
}

function onAnswer(chosenIndex: number): void {
  const s = session;
  if (!s || !awaitingAnswer) return;
  awaitingAnswer = false;
  clearPonderTimer();
  const q = s.engine.current as ServedQuestion;
  // submitAnswer also moves the engine's internal bankCents. We never read it,
  // never format it, and never post it — see the no-$ rule in the header.
  const result = s.engine.submitAnswer(chosenIndex, performance.now());
  s.answered++;
  if (result.wasCorrect) s.correct++;
  maybeTheoPop(result.wasCorrect ? 'correct' : 'miss');

  track('QuestionAnswered', {
    mode: 'friendly',
    code: stableCodeFor(q.id),
    correct: result.wasCorrect,
    ms: Math.round(result.elapsedMs),
    category: q.category,
  });

  setHidden('frHint', true); // "Choose an answer." has been obeyed
  const grid = el('frOptions');
  if (grid) {
    grid.querySelectorAll('.opt').forEach((b, i) => {
      (b as HTMLButtonElement).disabled = true;
      if (i === result.correctIndex) b.classList.add('correct');
      else if (i === chosenIndex) b.classList.add('wrong');
      else b.classList.add('dim');
    });
  }

  // The citation IS the payload here — it always shows, right or wrong.
  const reveal = el('frReveal');
  if (reveal) {
    const verdict = result.wasCorrect
      ? `<div class="fr-verdict ok">${icon('check', 30)} That&rsquo;s correct.</div>`
      : `<div class="fr-verdict no">The answer is <b>${esc(q.options[result.correctIndex])}</b></div>`;
    reveal.innerHTML = verdict + `<div class="cite">${esc(result.citation)}</div>`;
  }

  const last = s.engine.idx + 1 >= s.engine.n;
  const next = el('frNextBtn');
  if (next) next.textContent = last ? 'See how you did →' : 'Next question →';
  setHidden('frNextBtn', false);
}

function onNext(): void {
  const s = session;
  if (!s) return;
  if (s.engine.idx + 1 >= s.engine.n) showEndCard();
  else serve();
}

/** End of a set: correct-count and (2026-08-03) the XP it earned. Still no dollars,
    no delta, no bank — that part of the rule stands. */
function showEndCard(): void {
  const s = session;
  if (!s) return;
  track('FriendlySessionEnded', {
    mode: 'friendly',
    area: s.area,
    level: s.choice,
    correct: s.correct,
    answered: s.answered,
  });

  // Authoritative XP credit lives server-side (usp_Stats_AwardXp, db/038) — this is
  // fire-and-forget, same as the telemetry beats above: the end card never blocks
  // on it, and a failed post just means this one set's XP wasn't banked.
  if (s.correct > 0 && isAuthenticated()) {
    awardFriendlyXp(s.correct, s.answered).catch(() => {
      /* offline / server error — nothing to recover client-side, no local XP total to roll back */
    });
  }

  setHidden('frBody', true);
  setHidden('frOptions', true);
  setHidden('frNextBtn', true);
  const reveal = el('frReveal');
  if (reveal) reveal.innerHTML = '';
  const host = el('frEnd');
  if (!host) return;
  const next = nextStudyArea(bundledStudyAreas(), s.area, s.choice);
  const perfect = s.answered > 0 && s.correct === s.answered;
  const xpLine =
    s.correct > 0 ? `<div class="fr-end-xp">+${s.correct * FRIENDLY_XP_PER_CORRECT} XP</div>` : '';
  // "Next up: …" (2026-08-04, Eric: finished Scripture L2, clicked Next, ended up on
  // Doctrine · All levels without realizing it — nextStudyArea() can change BOTH the
  // area and, when the destination lacks the current level, the level choice too.
  // Naming the destination before the click removes the surprise; the picker's
  // checkmark logic itself was already correct (Doctrine has 43 questions — one
  // 10-question set was never going to complete it).
  const nextHint = next
    ? `<div class="fr-end-next-hint">Next up: ${esc(sessionLabel(next.area, next.choice))}</div>`
    : '';
  host.innerHTML =
    (perfect ? `<div class="fr-end-confetti" id="frEndConfetti"></div>` : '') +
    `<div class="fr-end-score">${esc(scoreLine(s.correct, s.answered, s.area, s.choice))}</div>` +
    xpLine +
    `<div class="fr-end-note">${esc(encouragement(s.correct, s.answered))}</div>` +
    nextHint +
    `<div class="fr-end-row">` +
    `<button class="btn fr-end-sm" id="frKeepBtn" type="button"><span class="fr-end-ic">↺</span>Replay Level</button>` +
    (next
      ? `<button class="btn fr-end-sm" id="frNextAreaBtn" type="button">Next<span class="fr-end-ic">→</span></button>`
      : '') +
    `</div>` +
    `<button class="btn secondary" id="frDoneBtn" type="button">Done</button>`;
  setHidden('frEnd', false);
  if (perfect) {
    const confetti = el('frEndConfetti');
    if (confetti) friendlyConfetti(confetti);
    triggerCharPop(getLine('theo', 'correct'));
  } else {
    maybeTheoPop('finish-imperfect');
  }
  const keep = el('frKeepBtn');
  if (keep) keep.addEventListener('click', () => startFriendlySession(s.area, s.choice));
  const nextBtn = el('frNextAreaBtn');
  if (nextBtn && next) nextBtn.addEventListener('click', () => startFriendlySession(next.area, next.choice));
  const done = el('frDoneBtn');
  if (done) done.addEventListener('click', goHome);
}

/* --------------------------------- exits --------------------------------- */

function goHome(): void {
  session = null;
  awaitingAnswer = false;
  clearPonderTimer();
  hideBank();
  show('home');
}

function backToPicker(): void {
  session = null;
  awaitingAnswer = false;
  openFriendlyPlay();
}

/* --------------------------------- public --------------------------------- */

/** Open the study-area picker (home button, hub tile). Guests included. */
export function openFriendlyPlay(): void {
  if (!isFriendlyPlayEnabled()) return;
  track('AreaOpened', { area: 'friendly' });
  hideBank();
  renderPicker();
  show('friendly');
  maybeShowTutorial('friendly'); // first encounter only: "tap a Type, then a Complexity"
}

/** Answer the visible study question by 1-based option number (keyboard). */
export function friendlyAnswerByKey(n: number): void {
  const grid = el('frOptions');
  if (!grid) return;
  const b = grid.querySelectorAll<HTMLButtonElement>('.opt')[n - 1];
  if (b) b.click();
}

/** Advance the visible study card (keyboard Enter). */
export function friendlyAdvance(): void {
  const next = el('frNextBtn');
  if (next && !next.classList.contains('hidden')) {
    next.click();
    return;
  }
  const keep = el('frKeepBtn');
  if (keep) keep.click();
}

/** Wire the Friendly Play entry points once at boot. With the flag off the home
    button and hub tile stay hidden, so the mode has no reachable entry. */
export function initFriendlyPlay(): void {
  const on = isFriendlyPlayEnabled();
  setHidden('friendlyBtn', !on);
  if (!on) return;
  el('friendlyBtn')?.addEventListener('click', openFriendlyPlay);
  el('tileFriendly')?.addEventListener('click', openFriendlyPlay);
  el('frBackBtn')?.addEventListener('click', goHome);
  el('frQuitBtn')?.addEventListener('click', backToPicker);
  el('frNextBtn')?.addEventListener('click', onNext);
}
